import { createSlice } from "@reduxjs/toolkit";
import { status } from "../../_constants/common";
import { getMerchantAnalytics } from "./DashboardThunk";

// `reqId` holds the requestId of the fetch currently in flight for this section. Responses that
// don't match it are stale (an older, slower request landing after a newer one) and are dropped —
// without this, switching a filter twice quickly could leave the table showing the FIRST result
// while the filter pill shows the second.
const emptySection = (period = "all_time") => ({
  status: null,
  data: null,
  error: null,
  period,
  from: null,
  to: null,
  reqId: null,
});

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState: {
    // Each analytics section is filtered + fetched independently.
    analytics: {
      // Combined earnings + orders under ONE period filter.
      overview: emptySection("all_time"),
      // deals also carries the Most Sold Deals table filters (limit + status).
      deals: { ...emptySection("all_time"), dealLimit: "top5", dealStatus: "all" },
      // Monthly orders/revenue chart — filtered by year (not period).
      monthly: { status: null, data: null, error: null, year: null, reqId: null },
    },
  },
  reducers: {
    // Update a section's filter (period + optional custom from/to) before refetching.
    setSectionFilter: (state, { payload }) => {
      const { section, period, from = null, to = null } = payload;
      if (!state.analytics[section]) return;
      state.analytics[section].period = period;
      state.analytics[section].from = from;
      state.analytics[section].to = to;
    },
    // Monthly chart year selection — drives the year pill immediately, before the response lands.
    setMonthlyYear: (state, { payload }) => {
      state.analytics.monthly.year = payload ? Number(payload) : null;
    },
    // Most Sold Deals table filters (limit: top5|all, status: all|active|paused|expired).
    setDealTableFilter: (state, { payload }) => {
      const { dealLimit, dealStatus } = payload;
      if (dealLimit !== undefined) state.analytics.deals.dealLimit = dealLimit;
      if (dealStatus !== undefined) state.analytics.deals.dealStatus = dealStatus;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMerchantAnalytics.pending, (state, { meta }) => {
        const sec = meta?.arg?.section || "overview";
        if (!state.analytics[sec]) return;
        state.analytics[sec].reqId = meta.requestId; // this is now the only fetch we'll accept
        state.analytics[sec].status = status.IN_PROGRESS;
        state.analytics[sec].error = null;
      })
      .addCase(getMerchantAnalytics.fulfilled, (state, { payload, meta }) => {
        const sec = payload?.section || meta?.arg?.section || "overview";
        const slice = state.analytics[sec];
        if (!slice || slice.reqId !== meta.requestId) return; // stale response — ignore
        slice.status = status.SUCCESS;
        slice.data = payload.payload;
        slice.error = null;
        slice.reqId = null;
      })
      .addCase(getMerchantAnalytics.rejected, (state, { payload, meta }) => {
        const sec = meta?.arg?.section || "overview";
        const slice = state.analytics[sec];
        if (!slice || slice.reqId !== meta.requestId) return; // stale rejection — ignore
        slice.status = status.FAILURE;
        slice.error = payload || "Something went wrong";
        // Drop stale data so the UI can't show OLD figures under the NEW filter label.
        slice.data = null;
        slice.reqId = null;
      });
  },
});

export const { setSectionFilter, setMonthlyYear, setDealTableFilter } = dashboardSlice.actions;
export default dashboardSlice.reducer;
